"use client";

/**
 * train-dojo-hero.tsx — Setup 首屏「今日道场」
 *
 * §0 视觉优先:到期数大字金显(回来的钩子)+ 本周 7 格点阵(只正向点亮,缺席不指责)
 * + 一键开始复习 / 空态「第 N 天 · 先练 10 题打底」+ 最近 8 组正确率 sparkline。
 */

import { useEffect, useMemo, useSyncExternalStore } from "react";
import {
  animate,
  motion,
  useMotionValue,
  useReducedMotion,
  useTransform,
} from "motion/react";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/cn";
import { DISPLAY_FONT, EASE_OUT } from "@/components/dojo-theme";
import {
  EMPTY_TRAIN_LOG,
  getTrainLogSnapshot,
  subscribeTrainLog,
  type SessionSummary,
} from "@/lib/train-log";

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** 大数字滚动(motion value,不触发 React 重渲染;reduce 时瞬时) */
function CountUp({ value, reduce }: { value: number; reduce: boolean }) {
  const mv = useMotionValue(0);
  const text = useTransform(mv, (v) => String(Math.round(v)));
  useEffect(() => {
    if (reduce) {
      mv.set(value);
      return;
    }
    const controls = animate(mv, value, { duration: 0.7, ease: EASE_OUT });
    return () => controls.stop();
  }, [value, reduce, mv]);
  if (reduce) return <>{value}</>;
  return <motion.span>{text}</motion.span>;
}

/** 本周 7 格点阵:在场暖金点亮,缺席暗灰,今天略大带描边。只点亮,不惩罚。 */
function WeekDots({ days, reduce }: { days: string[]; reduce: boolean }) {
  const presence = useMemo(() => {
    const daySet = new Set(days);
    const out: boolean[] = [];
    const anchor = new Date();
    anchor.setHours(12, 0, 0, 0);
    for (let i = 6; i >= 0; i--) {
      const d = new Date(anchor);
      d.setDate(d.getDate() - i);
      out.push(daySet.has(isoDate(d)));
    }
    return out;
  }, [days]);

  return (
    <div className="flex items-center gap-2" aria-hidden>
      {presence.map((lit, i) => {
        const isToday = i === presence.length - 1;
        return (
          <motion.span
            key={i}
            className={cn(
              "rounded-full",
              isToday ? "size-3.5" : "size-2.5",
              lit
                ? "bg-amber-300 shadow-[0_0_10px_rgba(252,211,77,0.55)]"
                : "bg-[#223329]",
              isToday && "ring-1 ring-amber-300/50 ring-offset-2 ring-offset-[#0D1510]",
            )}
            initial={reduce ? false : { opacity: 0, scale: 0.3 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={
              reduce
                ? { duration: 0 }
                : { type: "spring", stiffness: 320, damping: 18, delay: 0.25 + i * 0.05 }
            }
          />
        );
      })}
    </div>
  );
}

/** 最近 N 组正确率 sparkline(柱形,延续 stats 页 trend 语言;末组暖金) */
function AccuracySpark({ sessions, reduce }: { sessions: SessionSummary[]; reduce: boolean }) {
  const pts = sessions.map((s) => (s.total > 0 ? s.correct / s.total : 0));
  const lastPct = Math.round((pts[pts.length - 1] ?? 0) * 100);
  return (
    <div className="relative border-t border-white/5 px-5 pb-4 pt-3.5 md:px-7">
      <div className="flex items-baseline justify-between">
        <span className="text-[10px] font-medium tracking-[0.18em] text-stone-500">
          最近 {sessions.length} 组正确率
        </span>
        <span className="font-mono text-xs tabular-nums text-amber-300/90">{lastPct}%</span>
      </div>
      <div className="mt-2.5 flex h-9 items-end gap-1" aria-hidden>
        {pts.map((p, i) => {
          const isLast = i === pts.length - 1;
          return (
            <motion.span
              key={i}
              title={`${Math.round(p * 100)}%`}
              className={cn(
                "flex-1 origin-bottom rounded-t-[3px]",
                isLast
                  ? "bg-amber-300 shadow-[0_0_12px_rgba(252,211,77,0.35)]"
                  : p >= 0.7
                    ? "bg-emerald-400/70"
                    : p >= 0.4
                      ? "bg-emerald-400/40"
                      : "bg-[#2E4437]",
              )}
              style={{ height: `${Math.max(8, p * 100)}%` }}
              initial={reduce ? false : { scaleY: 0.15, opacity: 0.4 }}
              animate={{ scaleY: 1, opacity: 1 }}
              transition={
                reduce
                  ? { duration: 0 }
                  : { type: "spring", stiffness: 260, damping: 24, delay: 0.3 + i * 0.05 }
              }
            />
          );
        })}
      </div>
    </div>
  );
}

export function TodayDojoHero({
  dueCount,
  onStartDue,
  onStartFresh,
}: {
  /** 到期可复习的场景数(场景,不是概念) */
  dueCount: number;
  /** 一键开始复习:queue = 到期场景池 */
  onStartDue: () => void;
  /** 空态:默认 mixed/beginner 10 题打底 */
  onStartFresh: () => void;
}) {
  const reduce = useReducedMotion() ?? false;
  // localStorage 真源:useSyncExternalStore + 空服务器快照,hydration 安全
  const trainLog = useSyncExternalStore(
    subscribeTrainLog,
    getTrainLogSnapshot,
    () => EMPTY_TRAIN_LOG,
  );
  const dayN = Math.max(1, trainLog.days.length);
  const spark = trainLog.sessions.slice(-8);
  const hasDue = dueCount > 0;

  return (
    <motion.section
      initial={reduce ? { opacity: 0 } : { opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={reduce ? { duration: 0.2 } : { duration: 0.55, ease: EASE_OUT }}
      className={cn(
        "relative overflow-hidden rounded-2xl border bg-[#0D1510]/80",
        hasDue ? "border-amber-300/25" : "border-[#223329]",
      )}
    >
      {/* 灯下光晕:到期=暖金,空态=翡翠 */}
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-0",
          hasDue
            ? "bg-[radial-gradient(ellipse_70%_90%_at_18%_-10%,rgba(228,184,91,0.16),transparent_62%)]"
            : "bg-[radial-gradient(ellipse_70%_90%_at_18%_-10%,rgba(52,211,153,0.09),transparent_62%)]",
        )}
      />

      <div className="relative flex flex-wrap items-center gap-x-10 gap-y-5 p-5 md:p-7">
        {/* 左:大字 + CTA */}
        <div className="min-w-0 flex-1">
          <p
            className={cn(
              "text-[11px] font-semibold tracking-[0.24em]",
              hasDue ? "text-amber-300/70" : "text-emerald-300/70",
            )}
          >
            今日道场
          </p>

          {hasDue ? (
            <>
              <div className="mt-2 flex items-baseline gap-2.5">
                <span className="text-sm font-medium text-stone-400">今日待复习</span>
                <motion.span
                  className={cn(
                    DISPLAY_FONT,
                    "text-6xl font-extrabold leading-[0.9] tracking-tight text-amber-300 md:text-7xl",
                  )}
                  initial={reduce ? false : { opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={
                    reduce
                      ? { duration: 0 }
                      : { type: "spring", stiffness: 240, damping: 20, delay: 0.1 }
                  }
                >
                  <CountUp value={dueCount} reduce={reduce} />
                </motion.span>
                <span className="text-sm font-medium text-stone-400">题</span>
              </div>
              <button
                type="button"
                onClick={onStartDue}
                className="group mt-5 inline-flex items-center gap-2 rounded-xl bg-amber-300 px-7 py-3 text-sm font-bold text-[#3A2A05] shadow-[0_10px_36px_-10px_rgba(252,211,77,0.5)] transition-all duration-150 hover:bg-amber-200 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#090E0B]"
              >
                开始复习{" "}
                <span className="font-mono tabular-nums">{dueCount}</span> 题
                <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-0.5" />
              </button>
            </>
          ) : (
            <>
              <div className="mt-2 flex items-baseline gap-1.5">
                <span className="text-sm font-medium text-stone-400">第</span>
                <motion.span
                  className={cn(
                    DISPLAY_FONT,
                    "text-6xl font-extrabold leading-[0.9] tracking-tight text-stone-50 md:text-7xl",
                  )}
                  initial={reduce ? false : { opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={
                    reduce
                      ? { duration: 0 }
                      : { type: "spring", stiffness: 240, damping: 20, delay: 0.1 }
                  }
                >
                  {dayN}
                </motion.span>
                <span className="text-sm font-medium text-stone-400">天</span>
              </div>
              <p className="mt-2 text-sm text-stone-400">先练 10 题打底</p>
              <button
                type="button"
                onClick={onStartFresh}
                className="group mt-4 inline-flex items-center gap-2 rounded-xl bg-emerald-400 px-7 py-3 text-sm font-bold text-[#062015] shadow-[0_10px_36px_-10px_rgba(52,211,153,0.5)] transition-all duration-150 hover:bg-emerald-300 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#090E0B]"
              >
                开始今日训练
                <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-0.5" />
              </button>
            </>
          )}
        </div>

        {/* 右:本周点阵 */}
        <div className="flex shrink-0 flex-col items-end gap-2.5">
          <span className="text-[10px] font-medium tracking-[0.18em] text-stone-500">
            本周
          </span>
          <WeekDots days={trainLog.days} reduce={reduce} />
        </div>
      </div>

      {/* 最近 8 组正确率:<2 组不显示 */}
      {spark.length >= 2 && <AccuracySpark sessions={spark} reduce={reduce} />}
    </motion.section>
  );
}
